import { Injectable, inject } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DynaFields, DynaFieldType, DynaStatus } from '@/types';
import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { getFieldEditorFormObject } from './utils/field-editor-form-object';
import { getFieldEditorDrawerObject } from './utils/field-editor-drawer-object';

interface SystemType {
  value: string;
  label: string;
}

type SystemSubType = Record<string, SystemType[]>;

interface TableDetailForm {
  columnName: string;
  columnPos: number;
  type: DynaFieldType;
  source?: string;
  sourceField?: string;
}

@Injectable({ providedIn: 'root' })
export class FieldEditorService {
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);

  // Internal state
  private dynaFieldsId = '';
  private dynaDocsId = '';
  private dynaDocsVer = '';
  private ref = '';
  private isNew = true;

  // Observable state
  readonly label$ = new BehaviorSubject<string>('');
  readonly name$ = new BehaviorSubject<string>('');
  readonly description$ = new BehaviorSubject<string>('');
  readonly type$ = new BehaviorSubject<SelectOption | undefined>(undefined);
  readonly status$ = new BehaviorSubject<SelectOption | undefined>({ name: 'Actif', code: DynaStatus.ACTIF });
  readonly order$ = new BehaviorSubject<number>(0);
  readonly source$ = new BehaviorSubject<SelectOption | undefined>(undefined);
  readonly sourceField$ = new BehaviorSubject<SelectOption | undefined>(undefined);
  readonly currentType$ = new BehaviorSubject<DynaFieldType | null>(null);

  detailsFormArray: FormArray = this.fb.array([]);

  readonly statusOptions: SelectOption[] = [
    { name: 'Actif', code: DynaStatus.ACTIF },
    { name: 'Non Actif', code: DynaStatus.NON_ACTIF },
  ];

  readonly fieldTypeOptions: SelectOption[] = Object.values(DynaFieldType).map(type => ({
    code: type,
    name: this.getTypeDescription(type),
  }));

  readonly systemTypeOptions: SelectOption[] = [
    { code: 'ppm', name: 'PPM' },
    { code: 'ac', name: 'AC' },
    { code: 'realisation', name: 'Réalisation' },
    { code: 'ao', name: 'AO' },
    { code: 'dac', name: 'DAC' },
  ];

  readonly systemSubTypes: SystemSubType = {
    ao: [
      { value: 'ref', label: 'Référence AO' },
      { value: 'nom', label: 'Nom AO' },
      { value: 'description', label: 'Description AO' },
    ],
    dac: [
      { value: 'ref', label: 'Référence DAC' },
      { value: 'payant', label: 'Payant' },
      { value: 'montant', label: 'Montant' },
      { value: 'first_name_laste_name_responable_dac', label: 'Nom et Prénom Responsable' },
      { value: 'tel_responable_dac', label: 'Tél Responsable' },
      { value: 'email_responable_dac', label: 'Email Responsable' },
      { value: 'adresse_responable_dac', label: 'Adresse Responsable' },
      { value: 'condition_participation', label: 'Conditions de participation' },
      { value: 'date_limite_soumission', label: 'Date limite soumission des demandes' },
      { value: 'heure_limite_soumission', label: 'Heure limite de soumission' },
      { value: 'date_limite_reception_demande', label: 'Date limite réception des demandes' },
      { value: 'date_ouverture', label: "Date d'ouverture" },
      { value: 'heure_ouverture', label: "Heure d'ouverture" },
      { value: 'date_debut_depot', label: "Date de début de depot" },
      { value: 'date_creation', label: 'Date de création' },
    ],
    ppm: [
      { value: 'ref', label: 'Référence PPM' },
      { value: 'date_publication', label: 'Date publication PPM' },
    ],
    ac: [
      { value: 'nom', label: 'Nom AC' },
      { value: 'adresse', label: 'Adresse AC' },
      { value: 'email', label: 'Email AC' },
      { value: 'tel1', label: 'Tél 1 AC' },
      { value: 'tel2', label: 'Tél 2 AC' },
    ],
    realisation: [
      { value: 'realisation_envisagees', label: 'Objet Réalisation' },
      { value: 'beneficiaire', label: 'Beneficiaire' },
      { value: 'type_marcher', label: 'Type de marché' },
      { value: 'budget_estime', label: 'Budget estime' },
      { value: 'source_financement', label: 'Source(s) de financement' },
      { value: 'mode_passation', label: 'Mode de Passation' },
      { value: 'nom_lot', label: 'Lots' },
      { value: 'date_prev_lps', label: 'Date prévue de lancement' },
      { value: 'date_prev_ac', label: "Date prévue d'attribution du contrat" },
      { value: 'date_prev_dp', label: 'Date prévue de démarrage des prestations' },
      { value: 'date_prev_ap', label: "Date prévue d'achèvement des prestations " },
    ],
  };

  readonly columnTypeOptions: SelectOption[] = [
    { code: DynaFieldType.TEXT, name: this.translateService.instant('dynamicDocument.form.text') },
    { code: DynaFieldType.INTEGER, name: this.translateService.instant('dynamicDocument.form.number') },
  ];

  private getTypeDescription(type: DynaFieldType): string {
    const descriptions: Record<DynaFieldType, string> = {
      [DynaFieldType.INTEGER]: this.translateService.instant('dynamicDocument.form.number'),
      [DynaFieldType.DECIMAL]: this.translateService.instant('dynamicDocument.form.decimal'),
      [DynaFieldType.TEXT]: this.translateService.instant('dynamicDocument.form.text'),
      [DynaFieldType.DATE]: this.translateService.instant('dynamicDocument.form.date'),
      [DynaFieldType.DATETIME]: this.translateService.instant('dynamicDocument.form.dateTime'),
      [DynaFieldType.TABLE]: this.translateService.instant('dynamicDocument.form.table'),
      [DynaFieldType.SYSTEM]: this.translateService.instant('dynamicDocument.form.system'),
      [DynaFieldType.LIST]: this.translateService.instant('dynamicDocument.form.list'),
    };
    return descriptions[type] || type.toString();
  }

  get isTableType(): boolean {
    return this.currentType$.value === DynaFieldType.TABLE;
  }

  get isListType(): boolean {
    return this.currentType$.value === DynaFieldType.LIST;
  }

  get isSystemType(): boolean {
    return this.currentType$.value === DynaFieldType.SYSTEM;
  }

  reset(): void {
    this.dynaFieldsId = '';
    this.dynaDocsId = '';
    this.dynaDocsVer = '';
    this.ref = '';
    this.isNew = true;
    this.label$.next('');
    this.name$.next('');
    this.description$.next('');
    this.type$.next(undefined);
    this.status$.next({ name: 'Actif', code: DynaStatus.ACTIF });
    this.order$.next(0);
    this.source$.next(undefined);
    this.sourceField$.next(undefined);
    this.currentType$.next(null);
    this.detailsFormArray = this.fb.array([]);
  }

  initializeFromField(field: DynaFields | null, isNew: boolean): void {
    this.reset();
    this.isNew = isNew;

    if (!field) return;

    this.dynaFieldsId = field.dynaFieldsId as any;
    this.dynaDocsId = field.dynaDocsId as any;
    this.dynaDocsVer = field.dynaDocsVer as any;
    this.ref = field.ref;

    this.label$.next(field.label || '');
    this.name$.next(field.name || '');
    this.description$.next(field.description || '');
    this.order$.next(field.dynaFieldsOrder || 0);

    if (field.type) {
      const typeOption = this.fieldTypeOptions.find(o => o.code === field.type);
      this.type$.next(typeOption);
      this.currentType$.next(field.type);
    }

    if (field.status) {
      const statusOption = this.statusOptions.find(o => o.code === field.status);
      this.status$.next(statusOption);
    }

    if (field.source) {
      const sourceOption = this.systemTypeOptions.find(o => o.code === field.source);
      this.source$.next(sourceOption);
    }

    if (field.sourceField) {
      const key = field.source;
      const subTypes = this.systemSubTypes[key] || [];
      const subTypeOption = subTypes.find(st => st.value === field.sourceField);
      if (subTypeOption) {
        this.sourceField$.next({ name: subTypeOption.label, code: subTypeOption.value });
      }
    }

    if (field.details?.length) {
      field.details.forEach(detail => {
        this.addDetail({
          ...detail,
          type: detail.type || DynaFieldType.TEXT,
        });
      });
    }
  }

  onTypeChange(): void {
    if ((this.isTableType || this.isListType) && this.detailsFormArray.length === 0) {
      this.addDetail();
    }

    if (!this.isSystemType) {
      this.source$.next(undefined);
      this.sourceField$.next(undefined);
    }
  }

  addDetail(detail?: Partial<TableDetailForm>): void {
    const detailGroup = this.fb.group({
      columnName: [detail?.columnName || '', Validators.required],
      columnPos: [detail?.columnPos || 0, Validators.required],
      type: [detail?.type || DynaFieldType.TEXT, Validators.required],
      source: [detail?.source || ''],
      sourceField: [detail?.sourceField || ''],
    });

    this.detailsFormArray.push(detailGroup);
  }

  removeDetail(index: number): void {
    this.detailsFormArray.removeAt(index);
  }

  getFormValue(): DynaFields | null {
    const label = this.label$.value;
    const name = this.name$.value;
    const type = this.type$.value?.code as DynaFieldType;
    const status = this.status$.value?.code as DynaStatus;
    const order = this.order$.value;

    if (!label || !name || !type || !status) {
      return null;
    }

    if (this.isSystemType && (!this.source$.value?.code || !this.sourceField$.value?.code)) {
      return null;
    }

    if ((this.isTableType || this.isListType) && this.detailsFormArray.invalid) {
      this.detailsFormArray.controls.forEach(c => c.markAsTouched());
      return null;
    }

    return {
      dynaFieldsId: this.dynaFieldsId as any,
      dynaDocsId: this.dynaDocsId as any,
      dynaDocsVer: this.dynaDocsVer as any,
      name,
      ref: this.ref,
      label,
      description: this.description$.value,
      type,
      source: (this.source$.value?.code as string) || '',
      sourceField: (this.sourceField$.value?.code as string) || '',
      dynaFieldsOrder: order,
      status,
      details:
        this.isTableType || this.isListType
          ? this.detailsFormArray.value.map((d: any) => ({
              ...d,
              type: d.type as DynaFieldType,
              source: d.source || null,
              sourceField: d.sourceField || null,
            }))
          : undefined,
    };
  }

  buildFormObject(): DynamicForm {
    return getFieldEditorFormObject({
      state: {
        label$: this.label$,
        name$: this.name$,
        description$: this.description$,
        type$: this.type$,
        status$: this.status$,
        order$: this.order$,
        source$: this.source$,
        sourceField$: this.sourceField$,
        currentType$: this.currentType$,
        detailsFormArray: this.detailsFormArray,
      },
      statusOptions: this.statusOptions,
      fieldTypeOptions: this.fieldTypeOptions,
      systemTypeOptions: this.systemTypeOptions,
      columnTypeOptions: this.columnTypeOptions,
      systemSubTypes: this.systemSubTypes,
      onTypeChange: () => this.onTypeChange(),
      onAddDetail: () => this.addDetail(),
      onRemoveDetail: (index: number) => this.removeDetail(index),
    });
  }

  buildDrawerObject(onSave: () => void, onCancel: () => void): DrawerObject {
    return getFieldEditorDrawerObject({
      isNew: this.isNew,
      formObject: this.buildFormObject(),
      onSave,
      onCancel,
    });
  }
}
